import React, { useCallback, useState, useEffect, useMemo } from "react";
import CustomTable from "@/components/common/table/CustomTable";
import dateHandler from "@/composables/dateUtil";
import EyeIcon from "@styles/all-images/icons/view-icon.svg";
import EditIcon from "@styles/all-images/icons/edit.svg";
import PlusIcon from "@styles/all-images/icons/plus.svg";
import DeleteIcon from "@styles/all-images/icons/delete-icon.svg";
import { useNavigate } from "react-router-dom";
import CreateFolder from "./CreateFolder";
import FileUpload, {
  SelectedDocument,
} from "@/components/common/inputs/FileUpload";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import actionsHandler from "@/store/actions/actionHandler";
import { AppThunkDispatch } from "@/store";
import FolderIcon from "@styles/all-images/icons/folderIcon.svg";
import FileIcon from "@styles/all-images/icons/file.svg";
import RightArrowIcon from "@styles/all-images/icons/plus.svg";
import { UserDocumentTabType } from "@/constants/enums/user-document-tab-type.enum";
import { NO_DATA_AVAILABLE } from "@/constants/constant-values";
import FilePreviewModal from "@/components/common/FilePreviewModal";
import { UserTierType } from "@/constants/enums/user-tier-type.enum";
interface IDocument {
  document_url: string;
  file_name: string;
}
export interface MasterDataTableProps {
  type: Partial<UserDocumentTabType>;
  tabId: number;
}

type ColumnConfig = {
  [key in UserDocumentTabType]?: {
    header: string;
    accessor: ((row: any) => any) | string;
    sortable: boolean;
    sortKey: string;
  }[];
};

interface ParentFolder {
  id: string;
  name: string;
}

// type ParentFolderType = ParentFolder[] | null;

const DocumentsTable: React.FC<MasterDataTableProps> = ({ type, tabId }) => {
  const navigate = useNavigate();
  const { formatDate } = dateHandler();
  const [forceReset, setForceReset] = useState(false);
  const [forceRefresh, setForceRefresh] = useState(false);
  const [columns, setColumns] = useState<any[]>([]);
  const dispatch = useDispatch<AppThunkDispatch>();
  const [isCreateFolderOpen, setIsCreateFolderOpen] = useState(false);
  const [initialData, setInitialData] = useState<any>(null);
  const [currentFolderId, setCurrentFolderId] = useState<
    string | number | null
  >(null);
  const [mode, setMode] = useState("create");
  const [parentFolder, setParentFolder] = useState<ParentFolder[] | null>(null);
  const [selectedDocument, setSelectedDocument] =
    useState<SelectedDocument | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [documents, setDocuments] = useState<IDocument[]>([]);
  const [userType, setUserType] = useState<UserTierType>(UserTierType.Tier1);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<any>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const titleConfig: Partial<
    Record<UserDocumentTabType, { title: string; display: string }>
  > = {
    [UserDocumentTabType.documentsToBeApproved]: {
      title: "Documents to be Approved",
      display: "documents to be approved",
    },
    [UserDocumentTabType.imDocument]: {
      title: "IM Document",
      display: "IM documents",
    },
    [UserDocumentTabType.termsSheet]: {
      title: "Terms Sheet",
      display: "terms sheet documents",
    },
    [UserDocumentTabType.myDrive]: {
      title: "My Drive",
      display: "items",
    },
  };

  const handleSettingColumns = useCallback(() => {
    const columnConfig: ColumnConfig = {
      [UserDocumentTabType.documentsToBeApproved]: [
        {
          header: "Company Name",
          accessor: (row: any) => row.company_name || NO_DATA_AVAILABLE,
          sortable: true,
          sortKey: "company_name",
        },
        {
          header: "Client Name",
          accessor: (row: any) => row.client_name || NO_DATA_AVAILABLE,
          sortable: true,
          sortKey: "client_name",
        },
        {
          header: "Review Pending",
          accessor: (row: any) => (
            <span>{row.review_pending || NO_DATA_AVAILABLE}</span>
          ),
          sortable: true,
          sortKey: "review_pending",
        },
        {
          header: "Uploaded on",
          accessor: (row: any) => formatDate(row.uploaded_on),
          sortable: true,
          sortKey: "uploaded_on",
        },
      ],
      [UserDocumentTabType.imDocument]: [
        {
          header: "Document Name",
          accessor: (row: any) => row.name || NO_DATA_AVAILABLE,
          sortable: true,
          sortKey: "name",
        },
        {
          header: "Uploaded on Date & Time",
          accessor: (row: any) => row.created_at,
          sortable: true,
          sortKey: "created_at",
        },
        {
          header: "Status",
          accessor: (row: any) => (
            <span
              className={`tw-px-2 tw-py-1 tw-rounded-full tw-text-sm ${row.status}`}
            >
              {row.status}
            </span>
          ),
          sortable: true,
          sortKey: "status",
        },
      ],
      [UserDocumentTabType.termsSheet]: [
        {
          header: "Document Name",
          accessor: "name",
          sortable: true,
          sortKey: "name",
        },
        {
          header: "Uploaded on Date & Time",
          accessor: (row: any) => row.created_at,
          sortable: true,
          sortKey: "created_at",
        },
        {
          header: "Send By",
          accessor: "sender_name",
          sortable: true,
          sortKey: "sender_name",
        },
        {
          header: "Status",
          accessor: (row: any) => (
            <span
              className={`tw-px-2 tw-py-1 tw-rounded-full tw-text-sm ${row.status}`}
            >
              {row.status}
            </span>
          ),
          sortable: true,
          sortKey: "status",
        },
      ],
      [UserDocumentTabType.myDrive]: [
        {
          header: "Name",
          accessor: (row: any) => (
            <div className="tw-flex tw-items-center tw-gap-2">
              <span className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-w-9 tw-h-9 tw-rounded-full tw-bg-[#F4EBFF]">
                <img
                  src={row.type === "file" ? FileIcon : FolderIcon}
                  alt={row.type === "file" ? "File Icon" : "Folder Icon"}
                  className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10 tw-max-w-5 tw-max-h-5"
                />
              </span>
              <span className="tw-truncate tw-max-w-[300px]">{row.name}</span>
            </div>
          ),
          sortable: true,
          sortKey: "name",
        },
        {
          header: "Uploaded on Date & Time",
          accessor: (row: any) => row.created_at,
          sortable: true,
          sortKey: "created_at",
        },
      ],
    };
    const baseColumns = columnConfig[type] || [];
    const actionColumn = getActionColumn(type);
    setColumns(actionColumn ? [...baseColumns, actionColumn] : baseColumns);
  }, [type]);

  const handleView = useCallback(
    (row: any, type?: UserDocumentTabType) => {
      if (row.type === "file") {
        setDocuments([row]);
        setShowPreview(true);
        return;
      }

      const currentPathName = window.location.pathname;
      const path =
        type === UserDocumentTabType.documentsToBeApproved
          ? `/t3/view-onboarding-documents/${row.user_id}?from=${currentPathName}`
          : `/document/my-drive/${row.id}`;

      navigate(path);
    },
    [navigate],
  );

  const handleEdit = useCallback((row: any) => {
    if (row.type === "file") {
      setMode("upload");
      setIsCreateFolderOpen(true);
      setSelectedDocument(row);
    } else {
      setInitialData(row);
      setMode("edit");
      setIsCreateFolderOpen(true);
    }
  }, []);

  const handleDelete = (row: any) => {
    setItemToDelete(row);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    setIsDeleting(true);

    try {
      const { authenticatedCall } = actionsHandler(
        itemToDelete.type === "file" ? "documentsDelete" : "folderDelete",
      );
      const response = await dispatch(
        authenticatedCall({
          customParams: [itemToDelete.id + "/"],
          method: "delete",
        }) as any,
      );

      if (response.ok) {
        toast.success(response.data.message || "Deleted successfully");
        setForceRefresh(true);
        fetchDocumentsWrapper({
          limit: 10,
          page: 1,
          search: "",
        });
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("An unexpected error occurred");
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
      setItemToDelete(null);
    }
  };

  const getActionColumn = useCallback(
    (type: UserDocumentTabType) => {
      switch (type) {
        case UserDocumentTabType.documentsToBeApproved:
          return {
            header: "View",
            accessor: (row: any) => (
              <div className="tw-flex tw-items-center tw-gap-2">
                <button
                  onClick={() =>
                    handleView(row, UserDocumentTabType.documentsToBeApproved)
                  }
                  title="View"
                >
                  <img
                    src={EyeIcon}
                    alt="View"
                    className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                  />
                </button>
              </div>
            ),
          };

        case UserDocumentTabType.imDocument:
          return {
            header: "Actions",
            accessor: (row: any) => (
              <div className="tw-flex tw-items-center tw-gap-2">
                <button onClick={() => handleView(row)} title="View">
                  <img
                    src={EyeIcon}
                    alt="View"
                    className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                  />
                </button>
                <button onClick={() => handleEdit(row)} title="Edit">
                  <img
                    src={EditIcon}
                    alt="Edit"
                    className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                  />
                </button>
              </div>
            ),
          };

        case UserDocumentTabType.termsSheet:
          return {
            header: "Action",
            accessor: (row: any) => (
              <div className="tw-flex tw-items-center tw-gap-2">
                <button onClick={() => handleView(row)} title="View">
                  <img
                    src={EyeIcon}
                    alt="View"
                    className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                  />
                </button>
              </div>
            ),
          };

        case UserDocumentTabType.myDrive:
          return {
            header: "Actions",
            accessor: (row: any) => (
              <div className="tw-flex tw-items-center">
                <button onClick={() => handleView(row)} title="View">
                  <img
                    src={EyeIcon}
                    alt="View"
                    className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10 tw-mr-2"
                  />
                </button>
                <button onClick={() => handleEdit(row)} title="Edit">
                  <img
                    src={EditIcon}
                    alt="Edit"
                    className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                  />
                </button>
                <button onClick={() => handleDelete(row)} title="Delete">
                  <img
                    src={DeleteIcon}
                    alt="Delete"
                    className="tw-w-auto tw-h-auto"
                  />
                </button>
              </div>
            ),
          };

        default:
          return null;
      }
    },
    [type, handleView, handleEdit, handleDelete],
  );
  const fetchDocumentsWrapper = useCallback(
    async (params: any) => {
      try {
        const { authenticatedCall } = actionsHandler(
          type === UserDocumentTabType.documentsToBeApproved
            ? "documentsToBeApproved"
            : "createFolder",
        );

        let queryString = new URLSearchParams({
          limit: params.limit.toString(),
          page: params.page.toString(),
          search: params.search || "",
          ...(params.sortColumn && {
            ordering:
              (params.sortDirection === "desc" ? "-" : "") + params.sortColumn,
          }),
        }).toString();

        // Add type-specific parameters
        if (type !== UserDocumentTabType.documentsToBeApproved) {
          queryString += `&id=${tabId}`;
        }

        const response = await dispatch(
          authenticatedCall({
            method: "get",
            customQuery: queryString,
          }),
        );

        // Handle response based on document type
        return type === UserDocumentTabType.documentsToBeApproved
          ? response
          : handleCustomResponse(response);
      } catch (error) {
        console.error(`Error fetching ${type} documents:`, error);
        return {
          data: {
            data: [],
            meta: { count: 0, page: 1, total_pages: 1 },
          },
        };
      }
    },
    [type, dispatch],
  );

  const handleCustomResponse = (response: any) => {
    const folders = response.data.data[0].children;
    const files = response.data.data[0].files.map((file: any) => ({
      ...file,
      type: "file",
      name: file.file_name,
    }));

    const data = [...folders, ...files];
    const meta = {
      ...response.data.meta,
      count: data.length,
    };
    return {
      ...response,
      data: { ...response.data, data: data, meta: meta },
    };
  };

  useEffect(() => {
    setColumns([]);
    setForceReset(true);
    setForceRefresh(true);
    handleSettingColumns();
    setCurrentFolderId(type === UserDocumentTabType.myDrive ? 1 : 2);
    setForceReset(true);
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const currentUserType = (user.user_type?.id ||
      UserTierType.Tier1) as UserTierType;
    setUserType(currentUserType);
  }, [type, handleSettingColumns, userType]);
  useEffect(() => {
    setForceRefresh(true);
  }, []);

  const renderBreadcrumb = () => {
    return "";
  };

  return (
    <>
      {renderBreadcrumb()}
      <CustomTable
        columns={columns}
        fetchData={fetchDocumentsWrapper}
        forceRefresh={forceRefresh}
        setForceRefresh={setForceRefresh}
        forceReset={forceReset}
        setForceReset={setForceReset}
        addButton={
          type === UserDocumentTabType.myDrive
            ? {
                text: "File Upload",
                onClick: () => {
                  setMode("upload");
                  setIsCreateFolderOpen(true);
                  setSelectedDocument(null);
                },
              }
            : undefined
        }
        secondaryAddButton={
          type === UserDocumentTabType.myDrive
            ? {
                text: "Create Folder",
                onClick: () => {
                  setIsCreateFolderOpen(true);
                  setInitialData(null);
                  setMode("create");
                  setCurrentFolderId(
                    type === UserDocumentTabType.myDrive ? 1 : 2,
                  );
                },
              }
            : type === UserDocumentTabType.imDocument &&
                userType === UserTierType.Tier3
              ? {
                  text: "Create IM Document",
                  onClick: () => {
                    setInitialData(null);
                    setMode("create");
                  },
                }
              : undefined
        }
        noDataMessage={`No ${titleConfig[type]?.display || "data"} found`}
        noSearchDataMessage={`No matching ${titleConfig[type]?.display || "data"} found`}
      />
      {isCreateFolderOpen && (
        <CreateFolder
          open={isCreateFolderOpen}
          onClose={() => setIsCreateFolderOpen(false)}
          mode={mode as "create" | "upload" | "edit"}
          parentId={currentFolderId}
          onSuccess={() => {
            setForceRefresh(true);
            setIsCreateFolderOpen(false);
          }}
          initialData={initialData}
          selectedDocument={selectedDocument || undefined}
        />
      )}
      {showPreview && (
        <FilePreviewModal
          document={documents}
          onClose={() => setShowPreview(false)}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="tw-fixed tw-inset-0 tw-bg-black/25 tw-flex tw-items-center tw-justify-center tw-z-50">
          <div className="tw-bg-white tw-rounded-lg tw-w-[400px]">
            <div className="tw-flex tw-justify-between tw-items-center tw-px-4 tw-py-4 tw-border-b">
              <div></div>
              <h2 className="tw-text-base tw-font-semibold">Confirm Delete</h2>
              <button onClick={() => setShowDeleteConfirm(false)}>
                <svg
                  className="tw-w-5 tw-h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="tw-p-4 tw-text-center tw-flex tw-justify-center tw-gap-2 tw-py-5 tw-px-6">
              <h6 className="tw-text-[#232323] tw-text-base tw-font-semibold tw-gap-1">
                Are you sure you want to delete this{" "}
                {itemToDelete?.type === "file" ? "file" : "folder"}?
                <span className="tw-inline-flex tw-items-center tw-justify-center"></span>
              </h6>
            </div>

            <div className="tw-flex tw-space-x-4 tw-py-5 tw-px-12">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
                className="tw-flex-1 tw-px-4 tw-py-2 tw-border font-default-primary-color tw-border-[#146C94] tw-rounded-lg tw-w-[50%] tw-text-center"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                className="tw-bg-blue-600 hover:tw-bg-blue-700 tw-flex tw-items-center tw-gap-2 tw-w-[50%] tw-justify-center tw-text-white tw-rounded-md tw-px-4 tw-py-2 primary-custom-button"
              >
                {isDeleting ? "Processing..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DocumentsTable;
